import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  COUNTRIES, NO_LOCATION, facilityRegionSet, isAllFacilities, levelName, scopeFacilities, scopeLabel,
  type DirectoryRow, type FacilityScope,
} from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useFacilityScope } from '../session/FacilityScope';
import { sx } from '../ui/designStyles';

/**
 * The facility switcher (V6, design scopeLayer): Operator and Super Admin choose what they are
 * looking at. Country, then that country's own levels, then one building. Choosing a
 * building never opens resident records. Super Admin moves a building between regions here.
 */

/** Pages that show the whole company whatever is picked (design CO). */
const COMPANY_WIDE = ['/sales', '/pulse'];
/**
 * Pages that follow the pick, with the note they show (empty: the whole page follows). A page
 * joins this list when it filters by the switcher (usePickedIds).
 */
export const FOLLOWS_SCOPE: Record<string, string> = {
  '/overview': 'Facilities, money and health follow this · other cards show every facility',
  '/capture': 'Claims and figures follow this · other tabs show every facility unless you pick one building',
  '/residents': '',
  '/guardian': '',
  '/worklist': '',
  // Chat and Eval are company-wide: those tabs say so (useCompanyWideView).
  '/atlas': '',
  '/performance': '',
  '/policy': '',
  // Finance › Aithora is company-wide: that scope says so (useCompanyWideView).
  '/finance': '',
};

/**
 * Policy with a place picked that is not one building: guard settings are set for the network
 * or for one building, so they stay the network's; the fires follow the pick.
 */
const POLICY_GROUP_NOTE = "Guard fires follow this · settings stay the network's until you pick one building";

/** The line beside the switcher when something is picked (design scPageNote). */
export function scopePageNote(path: string, scope: FacilityScope): string {
  if (isAllFacilities(scope)) return '';
  const on = (p: string) => path === p || path.startsWith(p + '/');
  if (COMPANY_WIDE.some(on)) return 'Company-wide page';
  const follows = Object.keys(FOLLOWS_SCOPE).find(on);
  if (follows === '/policy' && !scope.fac) return POLICY_GROUP_NOTE;
  if (follows !== undefined) return FOLLOWS_SCOPE[follows];
  return 'This page still shows every facility';
}

const LEVELS = ['country', 'region', 'state', 'city'] as const;
const plural = (n: number) => `${n} ${n === 1 ? 'facility' : 'facilities'}`;
const uniq = <T,>(a: T[]) => a.filter((x, i) => a.indexOf(x) === i);
const pd = (e?: MouseEvent) => e?.preventDefault();

const S = {
  wrapWide: 'position:relative;display:inline-flex;flex:0 1 auto;min-width:34px;',
  wrapNarrow: 'position:relative;display:inline-flex;flex:0 0 auto;',
  btn: 'display:inline-flex;align-items:center;justify-content:center;gap:7px;height:34px;box-sizing:border-box;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font-size:13px;font-weight:700;color:#22272E;text-decoration:none;white-space:nowrap;font-family:inherit;cursor:pointer;',
  btnWide: 'max-width:230px;min-width:0;padding:0 12px;',
  btnNar: 'width:132px;padding:0 9px;',
  btnIcon: 'width:34px;padding:0;',
  word: 'flex:0 0 auto;font-weight:600;color:#54606C;',
  label: 'min-width:0;overflow:hidden;text-overflow:ellipsis;',
  dot: 'flex:0 0 8px;width:8px;height:8px;border-radius:50%;background:#B3261E;',
  caret: 'flex:0 0 auto;color:#54606C;',
  pageNote: 'flex:0 1 auto;min-width:0;margin-left:8px;align-self:center;font-size:14px;font-weight:600;color:#54606C;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;',
  scrim: 'position:fixed;inset:0;z-index:95;background:transparent;border:0;padding:0;',
  panel: 'position:absolute;top:42px;left:0;z-index:96;width:380px;max-width:calc(100vw - 40px);box-sizing:border-box;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:12px;box-shadow:0 14px 40px rgba(34,39,46,.18);padding:12px;display:flex;flex-direction:column;gap:10px;white-space:normal;',
  panelNote: 'display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;background:rgba(179,38,30,.08);font-size:16px;font-weight:600;color:#8C2B2B;',
  search: 'box-sizing:border-box;width:100%;height:38px;padding:0 12px;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:14px;color:#22272E;',
  crumbs: 'display:flex;flex-wrap:wrap;align-items:center;gap:4px 6px;min-width:0;',
  crumbOn: 'font-size:16px;text-decoration:none;font-weight:700;color:#22272E;',
  crumb: 'font-size:16px;text-decoration:none;font-weight:600;color:#2F5C97;',
  sep: 'font-size:16px;color:#54606C;',
  col: 'display:flex;flex-direction:column;min-width:0;',
  head: 'font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;padding:0 8px 4px;',
  row: 'display:flex;align-items:center;gap:10px;min-height:40px;padding:0 8px;border-radius:7px;text-decoration:none;color:#22272E;',
  rowOn: 'background:rgba(47,92,151,.10);',
  rowName: 'flex:1 1 auto;min-width:0;font-size:14px;font-weight:600;',
  rowMeta: 'flex:0 0 auto;font-size:14px;color:#54606C;',
  chev: 'flex:0 0 auto;font-size:15px;color:#54606C;',
  more: 'font-size:14px;color:#54606C;padding:4px 8px 0;',
  none: 'font-size:13px;color:#3C454F;padding:0 8px;',
  editLink: 'align-self:flex-start;display:inline-flex;align-items:center;min-height:36px;padding:0 8px;font-size:13px;font-weight:700;color:#2F5C97;text-decoration:none;',
  locked: 'display:flex;align-items:center;gap:6px;padding:0 8px;font-size:14px;color:#54606C;',
  editBox: 'display:flex;flex-direction:column;gap:6px;padding:8px;border-radius:9px;background:rgba(47,92,151,.06);',
  editHeadRow: 'display:flex;align-items:center;gap:8px;',
  editHead: 'flex:1 1 auto;font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;',
  done: 'font-size:13px;font-weight:700;color:#2F5C97;text-decoration:none;',
  editList: 'display:flex;flex-direction:column;max-height:220px;overflow-y:auto;',
  editRow: 'display:flex;align-items:center;gap:8px;min-height:40px;border-top:1px solid rgba(34,39,46,.08);',
  editName: 'display:block;font-size:13px;font-weight:700;color:#22272E;',
  editWhere: 'display:block;font-size:14px;color:#54606C;',
  select: 'flex:0 0 auto;max-width:150px;height:34px;padding:0 8px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:13px;color:#22272E;',
  newRow: 'display:flex;gap:8px;',
  newInput: 'box-sizing:border-box;flex:1 1 auto;min-width:0;height:36px;padding:0 10px;border-radius:8px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:13px;color:#22272E;',
  addOn: 'flex:0 0 auto;display:inline-flex;align-items:center;height:36px;padding:0 14px;border-radius:8px;font-size:13px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;background:#264C7E;color:#FFFFFF;border:0;cursor:pointer;font-family:inherit;',
  addOff: 'flex:0 0 auto;display:inline-flex;align-items:center;height:36px;padding:0 14px;border-radius:8px;font-size:13px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;background:rgba(34,39,46,.08);color:#54606C;border:0;font-family:inherit;',
  err: 'font-size:14px;color:#8C2B2B;',
  editNote: 'font-size:14px;color:#3C454F;line-height:1.45;text-wrap:pretty;',
  foot: 'border-top:1px solid rgba(34,39,46,.1);padding:8px 8px 0;display:flex;flex-direction:column;gap:4px;',
  footNote: 'font-size:14px;color:#3C454F;line-height:1.45;text-wrap:pretty;',
  footSmall: 'font-size:14px;color:#54606C;',
};

export function FacilitySwitcher({ wide, path }: { wide: boolean; path: string }) {
  const { client } = useSession();
  const { enabled, scope, setScope, directory, reload, companyWide } = useFacilityScope();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(false);
  const [newRegion, setNewRegion] = useState('');
  const [added, setAdded] = useState<Record<string, string[]>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); setQ(''); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const all = directory.data?.facilities ?? [];
  const canEdit = directory.data?.canEditRegions === true;
  const inS = useMemo(() => scopeFacilities(all, scope) as DirectoryRow[], [all, scope]);
  if (!enabled) return null;

  const country = scope.country && COUNTRIES[scope.country] ? COUNTRIES[scope.country] : null;
  const depth = scope.fac ? 5 : scope.city ? 4 : scope.state ? 3 : scope.region ? 2 : scope.country ? 1 : 0;
  const narrowed = !!(scope.country || scope.fac);
  // A company-wide view inside a page that follows (Atlas Chat, Finance › Aithora) says so.
  const note = companyWide && !isAllFacilities(scope) ? 'Company-wide page' : scopePageNote(path, scope);
  // The count only once the list is in: while it loads, or if it cannot load, the place alone.
  const label = scopeLabel(scope) + (scope.fac || !directory.data ? '' : ` · ${inS.length}`);

  const go = (s: FacilityScope) => (e?: MouseEvent) => {
    pd(e);
    setScope(s);
    setQ('');
    setOpen(!s.fac);
  };

  const path_: Array<[string, FacilityScope]> = [['All facilities', {}]];
  if (scope.country) path_.push([country ? country.name : scope.country, { country: scope.country }]);
  if (scope.region) path_.push([scope.region, { country: scope.country, region: scope.region }]);
  if (scope.state) path_.push([scope.state, { country: scope.country, region: scope.region, state: scope.state }]);
  if (scope.city) path_.push([scope.city, { country: scope.country, region: scope.region, state: scope.state, city: scope.city }]);
  if (scope.fac) path_.push([scope.fac, scope]);

  let kidsHead = '';
  let kids: Array<{ label: string; meta: string; to: FacilityScope }> = [];
  if (depth < 4) {
    const key: (typeof LEVELS)[number] = LEVELS[depth as 0 | 1 | 2 | 3];
    kidsHead = key === 'country' ? 'Country' : levelName(scope.country, key);
    kids = uniq(inS.map((f) => f[key])).sort((a, b) => (a === NO_LOCATION ? 1 : b === NO_LOCATION ? -1 : a.localeCompare(b))).map((k) => ({
      label: depth === 0 ? (COUNTRIES[k]?.name ?? k) : k,
      meta: plural(inS.filter((f) => f[key] === k).length),
      to: { ...scope, [key]: k },
    }));
  }
  const ql = q.trim().toLowerCase();
  const pool = ql
    ? all.filter((f) => [f.name, f.city, f.state, f.region, COUNTRIES[f.country]?.name ?? f.country].join(' ').toLowerCase().includes(ql))
    : depth >= 2 ? inS : [];
  const facs = pool.slice(0, 8);

  // Region editing (Super Admin): the regions in use in this country, plus any typed in this session.
  const cc = scope.country && scope.country !== NO_LOCATION ? scope.country : 'US';
  const lvName = levelName(cc, 'region');
  const regions = uniq([...all.filter((f) => f.country === cc).map((f) => f.region), ...(added[cc] ?? [])])
    .filter((r) => r !== NO_LOCATION).sort();
  const editRows = all.filter((f) => f.country === cc && (!scope.region || f.region === scope.region));
  const nv = newRegion.trim().replace(/\s+/g, ' ');
  const dup = regions.some((r) => r.toLowerCase() === nv.toLowerCase());
  const move = async (f: DirectoryRow, region: string) => {
    setSaveError(null);
    try {
      await facilityRegionSet(client, { params: { orgId: f.id }, body: { region } });
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save the region');
    }
  };

  const btnStyle = S.btn + (wide ? S.btnWide : narrowed ? S.btnNar : S.btnIcon);
  return (
    <span style={sx(wide ? S.wrapWide : S.wrapNarrow)}>
      <button type="button" style={sx(btnStyle)} title={label} aria-label="Choose which facilities you are looking at" aria-expanded={open} aria-haspopup="dialog"
        onClick={() => { setOpen((o) => !o); setQ(''); }}>
        {wide && <span style={sx(S.word)}>Viewing</span>}
        {(wide || narrowed) && (
          <>
            <span style={sx(S.label)}>{label}</span>
            {!!note && !wide && <span title={note} style={sx(S.dot)} />}
            <span style={sx(S.caret)} aria-hidden="true">&#9662;</span>
          </>
        )}
        {!wide && !narrowed && (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 21V5.5L12 3l8 2.5V21" />
            <path d="M9 21v-4h6v4M8.5 8.5h1M14.5 8.5h1M8.5 12.5h1M14.5 12.5h1" />
          </svg>
        )}
      </button>
      {!!note && wide && <span style={sx(S.pageNote)}>{note}</span>}
      {open && (
        <>
          <button type="button" aria-label="Close" style={sx(S.scrim)} onClick={() => { setOpen(false); setQ(''); }} />
          <div role="dialog" aria-label="Facility switcher" data-screen-label="Facility switcher" style={sx(S.panel)}>
            {!!note && (
              <div role="status" style={sx(S.panelNote)}>
                <span style={sx(S.dot)} />
                <span style={sx('min-width:0;')}>{note}</span>
              </div>
            )}
            <input ref={searchRef} type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a facility, city or state" aria-label="Search facilities" style={sx(S.search)} />
            <div style={sx(S.crumbs)}>
              {path_.map(([l, s], i) => (
                <span key={`${i}-${l}`} style={sx('display:inline-flex;gap:6px;align-items:center;')}>
                  <a href="#" onClick={go(s)} style={sx(i === path_.length - 1 ? S.crumbOn : S.crumb)}>{l}</a>
                  {i < path_.length - 1 && <span style={sx(S.sep)}>&rsaquo;</span>}
                </span>
              ))}
            </div>
            {directory.loading && !directory.data && <span style={sx(S.none)}>Loading facilities…</span>}
            {directory.error && !directory.data && <span role="alert" style={sx(S.none)}>Could not load the facility list. {directory.error.message}</span>}
            {!ql && kids.length > 0 && (
              <div style={sx(S.col)}>
                <div style={sx(S.head)}>{kidsHead}</div>
                {kids.map((k) => (
                  <a key={k.label} href="#" onClick={go(k.to)} style={sx(S.row)}>
                    <span style={sx(S.rowName)}>{k.label}</span>
                    <span style={sx(S.rowMeta)}>{k.meta}</span>
                    <span style={sx(S.chev)} aria-hidden="true">&rsaquo;</span>
                  </a>
                ))}
              </div>
            )}
            {facs.length > 0 && (
              <div style={sx(S.col)}>
                <div style={sx(S.head)}>{ql ? 'Matches' : 'Facilities'}</div>
                {facs.map((f) => (
                  <a key={f.id} href="#" onClick={go({ country: f.country, region: f.region, state: f.state, city: f.city, fac: f.name })}
                    style={sx(S.row + (scope.fac === f.name ? S.rowOn : ''))} aria-current={scope.fac === f.name ? 'true' : undefined}>
                    <span style={sx(S.rowName)}>{f.name}</span>
                    <span style={sx(S.rowMeta)}>{f.city === NO_LOCATION ? NO_LOCATION : `${f.city}, ${f.state}`}</span>
                  </a>
                ))}
                {pool.length > 8 && <span style={sx(S.more)}>Showing 8 of {pool.length}. Type to narrow it down.</span>}
              </div>
            )}
            {!!ql && pool.length === 0 && <span style={sx(S.none)}>No facility matches “{q.trim()}”.</span>}
            {directory.data && canEdit && !editing && (
              <a href="#" onClick={(e) => { pd(e); setEditing(true); setNewRegion(''); }} style={sx(S.editLink)}>Edit {lvName.toLowerCase()}s</a>
            )}
            {directory.data && !canEdit && (
              <span style={sx(S.locked)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3" /></svg>
                Regions are set by Super Admin.
              </span>
            )}
            {canEdit && editing && (
              <div data-screen-label="Edit regions" style={sx(S.editBox)}>
                <div style={sx(S.editHeadRow)}>
                  <span style={sx(S.editHead)}>{lvName}s in {COUNTRIES[cc]?.name ?? cc}</span>
                  <a href="#" onClick={(e) => { pd(e); setEditing(false); setNewRegion(''); }} style={sx(S.done)}>Done</a>
                </div>
                <div style={sx(S.editList)}>
                  {editRows.map((f) => (
                    <div key={f.id} style={sx(S.editRow)}>
                      <span style={sx('flex:1 1 auto;min-width:0;')}>
                        <span style={sx(S.editName)}>{f.name}</span>
                        <span style={sx(S.editWhere)}>{f.city === NO_LOCATION ? NO_LOCATION : `${f.city}, ${f.state}`}</span>
                      </span>
                      <select key={`${f.id}|${f.region}`} defaultValue={f.region} aria-label={`${lvName} for ${f.name}`} style={sx(S.select)}
                        onChange={(e) => { void move(f, e.target.value); }}>
                        {!regions.includes(f.region) && <option value={f.region}>{f.region}</option>}
                        {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div style={sx(S.newRow)}>
                  <input type="text" value={newRegion} onChange={(e) => setNewRegion(e.target.value)} maxLength={40}
                    placeholder={`New ${lvName.toLowerCase()} name`} aria-label={`New ${lvName.toLowerCase()} name`} style={sx(S.newInput)} />
                  <button type="button" disabled={!nv || dup} style={sx(nv && !dup ? S.addOn : S.addOff)}
                    onClick={() => { if (!nv || dup) return; setAdded((p) => ({ ...p, [cc]: [...(p[cc] ?? []), nv] })); setNewRegion(''); }}>Add</button>
                </div>
                {!!nv && dup && <span style={sx(S.err)}>That {lvName.toLowerCase()} already exists.</span>}
                {saveError && <span role="alert" style={sx(S.err)}>{saveError}</span>}
                <span style={sx(S.editNote)}>
                  Moving a building changes what every page shows for that {lvName.toLowerCase()}. Only Super Admin can do this, and each move is logged. A new {lvName.toLowerCase()} is kept once a building is in it.
                </span>
              </div>
            )}
            <div style={sx(S.foot)}>
              <span style={sx(S.footNote)}>
                {country ? `Privacy rule here: ${country.privacy}. ` : ''}Choosing a facility never opens resident records: Residents still asks the building. Finance › Aithora, Sales and System Pulse always show every facility.
              </span>
              {/* Plain words (V7 scSample, without SAMPLE: this directory is real). */}
              <span style={sx(S.footSmall)}>A country appears once it has a facility.</span>
            </div>
          </div>
        </>
      )}
    </span>
  );
}
